import { createContext, ReactNode, useCallback } from 'react';

// project-imports
import config, { MenuOrientation, ThemeDirection, ThemeMode } from 'config';
import useLocalStorage from 'hooks/useLocalStorage';

// types
import { CustomizationProps, FontFamily, I18n, PresetColor } from 'types/config';

// initial state
const initialState: CustomizationProps = {
  ...config,
  onChangeContainer: () => {},
  onChangeLocalization: () => {},
  onChangeMode: () => {},
  onChangePresetColor: () => {},
  onChangeDirection: () => {},
  onChangeMiniDrawer: () => {},
  onChangeMenuOrientation: () => {},
  onChangeMenuCaption: () => {},
  onChangeFontFamily: () => {},
  onChangeContrast: () => {}
};

export const CONFIG_STORAGE_KEY = 'clac-dd-config-v1';

// Escribe el modo de tema directo en localStorage, sin pasar por React/setState. Se usa
// justo antes de un reload deliberado (ver ThemeModeToggle): si el cambio de modo pasara
// por el contexto, el árbol alcanza a re-renderizarse con el theme a medio reconstruir
// mientras el reload todavía no ocurre — se ve un parpadeo de colores mixtos antes de que
// la navegación reemplace el documento. Al escribir el storage directo, el documento
// actual nunca se entera del cambio: solo lo aplica el documento nuevo, ya limpio.
export function persistThemeMode(mode: ThemeMode) {
  if (typeof window === 'undefined') return;
  const raw = window.localStorage.getItem(CONFIG_STORAGE_KEY);
  let stored: Partial<CustomizationProps> = {};
  if (raw) {
    try {
      stored = JSON.parse(raw);
    } catch {
      stored = {};
    }
  }
  window.localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify({ ...config, ...stored, mode }));
}

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState);

type ConfigProviderProps = {
  children: ReactNode;
};

function ConfigProvider({ children }: ConfigProviderProps) {
  const [storedConfig, setConfig] = useLocalStorage(CONFIG_STORAGE_KEY, initialState);

  const onChangeContainer = useCallback(
    (container: string) => {
      setConfig((prev: CustomizationProps) => ({
        ...prev,
        container: container !== 'fluid'
      }));
    },
    [setConfig]
  );

  const onChangeLocalization = useCallback(
    (lang: I18n) => {
      setConfig((prev: CustomizationProps) => ({ ...prev, i18n: lang }));
    },
    [setConfig]
  );

  const onChangeMode = useCallback(
    (mode: ThemeMode) => {
      setConfig((prev: CustomizationProps) => ({ ...prev, mode }));
    },
    [setConfig]
  );

  const onChangePresetColor = useCallback(
    (theme: PresetColor) => {
      setConfig((prev: CustomizationProps) => ({ ...prev, presetColor: theme }));
    },
    [setConfig]
  );

  const onChangeDirection = useCallback(
    (direction: ThemeDirection) => {
      setConfig((prev: CustomizationProps) => ({ ...prev, themeDirection: direction }));
    },
    [setConfig]
  );

  const onChangeMiniDrawer = useCallback(
    (miniDrawer: boolean) => {
      setConfig((prev: CustomizationProps) => ({ ...prev, menuOrientation: MenuOrientation.VERTICAL, miniDrawer }));
    },
    [setConfig]
  );

  const onChangeMenuOrientation = useCallback(
    (layout: MenuOrientation) => {
      setConfig((prev: CustomizationProps) => ({
        ...prev,
        menuOrientation: layout,
        ...(layout === MenuOrientation.VERTICAL && { miniDrawer: false })
      }));
    },
    [setConfig]
  );

  const onChangeContrast = useCallback(
    (themeContrast: string) => {
      setConfig((prev: CustomizationProps) => ({ ...prev, themeContrast: themeContrast === 'contrast' }));
    },
    [setConfig]
  );

  const onChangeMenuCaption = useCallback(
    (menuCaption: string) => {
      setConfig((prev: CustomizationProps) => ({ ...prev, menuCaption: menuCaption === 'caption' }));
    },
    [setConfig]
  );

  const onChangeFontFamily = useCallback(
    (fontFamily: FontFamily) => {
      setConfig((prev: CustomizationProps) => ({ ...prev, fontFamily }));
    },
    [setConfig]
  );

  return (
    <ConfigContext.Provider
      value={{
        ...storedConfig,
        i18n: storedConfig.i18n === 'es' ? 'es' : 'en',
        onChangeContainer,
        onChangeLocalization,
        onChangeMode,
        onChangePresetColor,
        onChangeDirection,
        onChangeMiniDrawer,
        onChangeMenuOrientation,
        onChangeMenuCaption,
        onChangeFontFamily,
        onChangeContrast
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export { ConfigProvider, ConfigContext };
